import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, Trash2, Loader2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function AuthImg({ photoId, variant, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false; let obj;
    api.get(`/admin/photos/${photoId}/file?variant=${variant}`, { responseType: "blob" })
      .then((r) => { if (revoked) return; obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [photoId, variant]);
  if (!url) return <div className={`bg-sand-100 animate-pulse ${className}`} />;
  return <img src={url} alt="" className={className} />;
}

export default function TripPhotosPanel({ tripId }) {
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ caption: "", consent_person: "", consent_private_share: true, consent_ai_processing: false, consent_public_marketing: false });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [fileKey, setFileKey] = useState(0);

  const load = () => api.get(`/admin/trips/${tripId}/photos`).then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tripId]);

  const upload = async () => {
    if (!file) return toast.error("Choose a photo first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      await api.post(`/admin/trips/${tripId}/photos`, fd);
      toast.success("Photo uploaded (original kept; shared copy has location data removed)");
      setFile(null); setFileKey((k) => k + 1); setMeta({ ...meta, caption: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const patch = async (id, data) => {
    setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, ...data } : p)));
    try { await api.patch(`/admin/photos/${id}`, data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  const del = async (id) => { if (!window.confirm("Delete this photo?")) return; try { await api.delete(`/admin/photos/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const makeAi = async (id) => {
    if (!window.confirm("Generate an AI souvenir? It will be clearly labelled 'AI-created souvenir — not an actual trip photo'. Images are processed by OpenAI (gpt-image-1).")) return;
    setAiBusy(id);
    try { await api.post(`/admin/photos/${id}/ai-souvenir`); toast.success("AI souvenir created and labelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiBusy(null); }
  };
  const review = async (id, approve) => {
    try { await api.post(`/admin/photos/${id}/review`, { approve }); toast.success(approve ? "Photo approved" : "Photo rejected"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="photos-panel">
      <div className="text-xs text-charcoal-500 bg-sand-50 rounded-lg p-3 space-y-1">
        <p><ShieldCheck size={13} className="inline mr-1 text-terracotta" /> Original photos are preserved untouched. The <b>shared</b> copy has GPS/location metadata stripped automatically.</p>
        <p><Sparkles size={13} className="inline mr-1 text-terracotta" /> AI souvenirs are optional, require the owner's AI-processing consent, are processed by <b>OpenAI (gpt-image-1)</b>, and are always labelled as not-a-real-photo. They are never shown as evidence of a stop or activity.</p>
      </div>

      <div className="rounded-xl border border-sand-200 bg-white p-4 space-y-3">
        <input data-testid="photo-file" key={fileKey} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <div className="grid sm:grid-cols-2 gap-2">
          <input data-testid="photo-caption" className="border rounded-lg px-3 py-2 text-sm" placeholder="Caption (optional)" value={meta.caption} onChange={(e) => setMeta({ ...meta, caption: e.target.value })} />
          <input data-testid="photo-consent-person" className="border rounded-lg px-3 py-2 text-sm" placeholder="Who gave consent (name)" value={meta.consent_person} onChange={(e) => setMeta({ ...meta, consent_person: e.target.value })} />
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          {[["consent_private_share", "Private trip sharing"], ["consent_ai_processing", "AI image processing"], ["consent_public_marketing", "Public marketing"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2"><input data-testid={`photo-${k}`} type="checkbox" checked={meta[k]} onChange={(e) => setMeta({ ...meta, [k]: e.target.checked })} /> {label}</label>
          ))}
        </div>
        <button data-testid="photo-upload-btn" onClick={upload} disabled={busy} className="bg-terracotta text-white rounded-full px-5 py-2 text-sm flex items-center gap-1 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload photo
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {photos.map((p) => (
          <div key={p.id} className="rounded-xl border border-sand-200 bg-white overflow-hidden" data-testid={`photo-${p.id}`}>
            <AuthImg photoId={p.id} variant={p.ai_path ? "ai" : "shared"} className="w-full h-40 object-cover" />
            {p.ai_path && <div className="text-[10px] bg-charcoal-800 text-white px-2 py-1">{p.ai_label}</div>}
            <div className="p-3 space-y-2 text-xs">
              {p.caption && <div className="text-charcoal-700">{p.caption}</div>}
              <div className="flex items-center gap-2">
                {p.uploaded_by === "client" && <span className="px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">from client</span>}
                {p.relationship && <span className="px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">{p.relationship.replace(/_/g, " ")}</span>}
                {p.review_status && p.review_status !== "approved" && <span className={`px-2 py-0.5 rounded-full ${p.review_status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{p.review_status.replace(/_/g, " ")}</span>}
              </div>
              {p.review_status === "pending_review" && (
                <div className="flex gap-2">
                  <button data-testid={`photo-approve-${p.id}`} onClick={() => review(p.id, true)} className="bg-green-600 text-white rounded-full px-3 py-1">Approve</button>
                  <button data-testid={`photo-reject-${p.id}`} onClick={() => review(p.id, false)} className="border border-red-300 text-red-600 rounded-full px-3 py-1">Reject</button>
                </div>
              )}
              <div className="text-charcoal-400">Consent: {p.consent_person || "—"}</div>
              <div className="flex flex-col gap-1">
                {[["consent_private_share", "Private share"], ["consent_ai_processing", "AI processing"], ["consent_public_marketing", "Public marketing"]].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2"><input data-testid={`photo-${p.id}-${k}`} type="checkbox" checked={!!p[k]} onChange={(e) => patch(p.id, { [k]: e.target.checked })} /> {label}</label>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <button data-testid={`photo-ai-${p.id}`} disabled={!p.consent_ai_processing || aiBusy === p.id} onClick={() => makeAi(p.id)} className="bg-sand-100 text-charcoal-700 rounded-full px-3 py-1 flex items-center gap-1 disabled:opacity-40">
                  {aiBusy === p.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI souvenir
                </button>
                <button onClick={() => del(p.id)} className="text-charcoal-400 hover:text-red-600 ml-auto"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {photos.length === 0 && <p className="text-sm text-charcoal-500">No photos yet.</p>}
      </div>
    </div>
  );
}
